import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahExternalContributions } from '~constants/api';

import { Paginated, Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { SahContributionClientGroup } from '~support-at-home/models';

export type ContributionsResponse = Paginated<SahContributionClientGroup>;

export type ContributionBody = {
  client_id?: number;
  category_id: string;
  funding_source_id: number;
  contrib_proportion: number;
  start_date: string;
  end_date: string | null;
};

export type BulkUpdateBody = {
  ids: string[];
  contrib_proportion: number;
  effective_date: string;
};

// `updated` rows changed in place, `ended` rows were given an end date, `created` new rows were added.
export type BulkUpdateResult = { updated: number; created: number; ended: number };

export type ContributionUpdate = { id: string; body: Omit<ContributionBody, 'client_id'> };

const LIST = { type: 'sah-client-contributions', id: 'LIST' } as const;

export const sahClientContributionsApi = createApi({
  reducerPath: 'sahClientContributionsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahExternalContributions }),
  tagTypes: ['sah-client-contributions'],
  endpoints: (builder) => ({
    // `limit` must always be sent: without it the API answers with the legacy grouped array.
    getContributions: builder.query<ContributionsResponse, Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [LIST],
      transformResponse: (payload) => unwrap<ContributionsResponse>(payload),
    }),
    createContribution: builder.mutation<void, ContributionBody>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: [LIST],
    }),
    updateContribution: builder.mutation<void, ContributionUpdate>({
      query: ({ id, body }) => ({ url: `/${id}`, method: 'PUT', body }),
      invalidatesTags: [LIST],
    }),
    deleteContribution: builder.mutation<void, string>({
      query: (id) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: [LIST],
    }),
    bulkUpdateContributions: builder.mutation<BulkUpdateResult, BulkUpdateBody>({
      query: (body) => ({ url: '/bulk-update', method: 'POST', body }),
      transformResponse: (payload) => unwrap<BulkUpdateResult>(payload),
      invalidatesTags: [LIST],
    }),
  }),
});

export const {
  useGetContributionsQuery,
  useCreateContributionMutation,
  useUpdateContributionMutation,
  useDeleteContributionMutation,
  useBulkUpdateContributionsMutation,
} = sahClientContributionsApi;
